import React, { FC, Fragment } from 'react';
import { Col, Divider, Row, Space, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { TextField } from '~/components';

import { useBeneficiaryAccountsQuery } from '~/hooks/beneficiaryAccounts';
import { Beneficiary, BeneficiaryAccount } from '~/types';

import AccountActions from './AccountActions';
import { useService } from '~/hooks/services';

type Props = {
  beneficiary: Beneficiary;
};

const AccountList = ({ beneficiary }: Props): React.ReactElement => {
  const { data } = useBeneficiaryAccountsQuery(beneficiary.id);
  return (
    <>
      {data &&
        data.map((account, index) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={account.id || 'new'}>
              {index > 0 && <Divider />}
              <AccountFields beneficiary={beneficiary} account={account} />
            </Fragment>
          );
        })}
    </>
  );
};

interface AccountFieldsProps {
  beneficiary: Beneficiary;
  account: BeneficiaryAccount;
}

const AccountFields: FC<AccountFieldsProps> = (props) => {
  const { account, beneficiary } = props;
  const [t] = useTranslation('payouts');

  const serviceQuery = useService(account.serviceId);
  const getEnumValueLabel = (paramId: string, fieldValue: string | number) => {
    const parameters = serviceQuery.data?.parameters;
    if (!parameters) return fieldValue;
    const fieldConfig = parameters[paramId];
    if (fieldConfig?.type !== 'ENUM') return fieldValue;
    const enumConfig = fieldConfig.enum?.find((config) => config.value === fieldValue);
    if (!enumConfig) return fieldValue;
    return enumConfig.name;
  };

  return (
    <Space size={16} direction="vertical" style={fullWidth}>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <TextField label={t('accountName')}>
            {account.name}{' '}
            {account.primary ? (
              <Tag color="green">{t('primary')}</Tag>
            ) : (
              account.status === 'PENDING' &&
              account.setAsPrimary && <Tag>{t('setAsPrimaryTag')}</Tag>
            )}
          </TextField>
        </Col>
        <Col span={12}>
          <TextField label={t('accountStatus')}>{account.status}</TextField>
        </Col>
        <Col span={12}>
          <TextField label={t('transferType')}>{account.serviceId}</TextField>
        </Col>
        {Object.keys(account.parameters)?.map((paramId) => {
          const fieldValue = account.parameters[paramId].value;
          const fieldName = account.parameters[paramId].name;
          return (
            <Col span={12} key={fieldName}>
              <TextField label={fieldName}>{getEnumValueLabel(paramId, fieldValue)}</TextField>
            </Col>
          );
        })}
      </Row>
      <AccountActions beneficiary={beneficiary} beneficiaryAccount={account} />
    </Space>
  );
};

export default AccountList;

const fullWidth = { width: '100%' };
